import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Button, EmptyState, Input, Select, Spinner } from '../components/ui';
import { Cell, DataTable, Notice, Page, PageHeader, Row, Section, Stack }
  from '../components/Page';
import type { AuditEntryDto } from '../lib/types';

/**
 * Common actions, so the filter is a menu rather than a string the operator has to know
 * the exact spelling of. Anything not listed is still reachable through the free-text
 * entity filter.
 */
const ACTIONS = [
  'user.signed_in', 'user.sign_in_failed', 'session.reuse_detected',
  'form.created', 'form.published', 'form.closed', 'form.deleted',
  'response.deleted', 'response.exported', 'pii.viewed',
  'company.created', 'company.member_added',
  'invite.issued', 'invite.revoked',
  'embed.token_minted', 'webhook.created',
  'audit.searched',
];

/**
 * How many entries arrive per set.
 *
 * Small on purpose. An audit entry is read, not skimmed — each one carries an actor, a
 * target and often a JSON diff — so a set is what somebody can actually take in before
 * deciding whether to keep going, and the next set costs one scroll rather than a decision.
 */
const PAGE_SIZE = 10;

export function AuditLog() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');

  const {
    data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['audit', action, entityType],
    queryFn: ({ pageParam }) => api.audit.list({
      action: action || undefined,
      entityType: entityType || undefined,
      limit: PAGE_SIZE,
      cursor: pageParam,
    }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const entries = data?.pages.flatMap((page) => page.data) ?? [];
  const loadMore = useNearBottom(
    () => { if (hasNextPage && !isFetchingNextPage) void fetchNextPage(); },
    [hasNextPage, isFetchingNextPage],
  );

  return (
    <Page width="wide">
      <PageHeader
        icon="shield"
        title="Audit log"
        description="Append-only. Entries cannot be edited or deleted from the application,
                     and opening this page is itself recorded."
      />

      <Stack>
        <Section title="Filter">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs font-semibold">
              Action
              <Select
                className="min-w-56"
                value={action}
                onChange={(e) => setAction(e.target.value)}
              >
                <option value="">All actions</option>
                {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-semibold">
              Entity
              <Input
                value={entityType}
                placeholder="Form, Response, Company…"
                onChange={(e) => setEntityType(e.target.value)}
              />
            </label>

            {data && (
              <span className="pb-2 text-xs text-[var(--ff-text-muted)]">
                {/* "12 of more" rather than a total: counting every matching row is a second
                    query over the whole table, and nobody reading a log needs the total —
                    they need to know whether there is more. */}
                {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
                {hasNextPage && ' so far'}
              </span>
            )}
          </div>
        </Section>

        {isLoading && <Spinner label="Loading audit entries" />}

        {error && (
          <Notice tone="warning">
            You do not have permission to read the audit log. It needs{' '}
            <code>audit:read</code>, which is normally an admin-only permission.
          </Notice>
        )}

        {data && entries.length === 0 && (
          <EmptyState
            title="No matching entries"
            description="Try widening the filters, or check a different action."
          />
        )}

        {entries.length > 0 && (
          <>
            <DataTable headers={['When', 'Action', 'Actor', 'Target', 'Result']}>
              {entries.map((entry) => <AuditRow key={entry.id} entry={entry} />)}
            </DataTable>

            {/*
              The control is a real button, and scrolling to it presses it.

              A bare sentinel that loads on intersection is unreachable by keyboard and
              invisible to a screen reader — the list simply ends, with no way to say
              "there is more". Keeping the button and letting the observer click it means
              the mouse gets an endless list and everybody else gets a control.
            */}
            <div ref={loadMore} className="flex justify-center py-1">
              {hasNextPage ? (
                <Button
                  variant="ghost"
                  className="text-xs"
                  disabled={isFetchingNextPage}
                  onClick={() => void fetchNextPage()}
                >
                  {isFetchingNextPage
                    ? `Loading the next ${PAGE_SIZE}…`
                    : `Load ${PAGE_SIZE} more`}
                </Button>
              ) : (
                <span className="text-xs text-[var(--ff-text-muted)]">
                  That is every entry matching these filters.
                </span>
              )}
            </div>

            {/* Politely, so a reader is told more arrived without losing their place. */}
            <p aria-live="polite" className="ff-sr">
              {isFetchingNextPage
                ? 'Loading more audit entries'
                : `Showing ${entries.length} audit entries`}
            </p>
          </>
        )}
      </Stack>
    </Page>
  );
}

/**
 * Calls back when the returned ref scrolls into view.
 *
 * `rootMargin` fires it a screen early, so the next set is usually already there by the
 * time the reader reaches the end and the list does not visibly stall. Nothing else in the
 * app needs this yet, so it lives here rather than in a shared hooks file it would be the
 * only occupant of.
 */
function useNearBottom(onReach: () => void, deps: unknown[]) {
  const ref = useRef<HTMLDivElement>(null);
  const latest = useRef(onReach);
  latest.current = onReach;

  useEffect(() => {
    const node = ref.current;

    // IntersectionObserver is missing in some embedded webviews. The button below is the
    // whole feature there, which is exactly why it is a button.
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (records) => { if (records.some((r) => r.isIntersecting)) latest.current(); },
      { rootMargin: '400px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}

function AuditRow({ entry }: { entry: AuditEntryDto }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = Boolean(entry.beforeJson || entry.afterJson || entry.reason);
  const failed = entry.result !== 'Success';

  return (
    <>
      <Row>
        <Cell muted nowrap>
          {new Date(entry.occurredUtc).toLocaleString()}
        </Cell>
        <Cell>
          {hasDetail ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="flex items-center gap-1 font-mono text-xs hover:underline"
            >
              <Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={13} />
              {entry.action}
            </button>
          ) : (
            <span className="font-mono text-xs">{entry.action}</span>
          )}
        </Cell>
        <Cell>
          {entry.actorEmail ?? (
            <span className="text-[var(--ff-text-muted)]">{entry.actorType}</span>
          )}
        </Cell>
        <Cell>
          {entry.entityName ?? entry.entityType ?? (
            <span className="text-[var(--ff-text-muted)]">—</span>
          )}
        </Cell>
        <Cell>
          <span className={failed ? 'font-medium text-[var(--ff-danger)]' : ''}>
            {entry.result}
          </span>
        </Cell>
      </Row>

      {expanded && (
        <tr className="border-t border-[var(--ff-border)] bg-[var(--ff-surface-sunken)]">
          <td colSpan={5} className="px-3 py-3">
            {entry.reason && (
              <p className="mb-2 text-xs text-[var(--ff-text-muted)]">{entry.reason}</p>
            )}
            <div className="grid gap-3 md:grid-cols-2">
              <JsonBlock label="Before" json={entry.beforeJson} />
              <JsonBlock label="After" json={entry.afterJson} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function JsonBlock({ label, json }: { label: string; json?: string }) {
  if (!json) return null;

  let pretty = json;
  try { pretty = JSON.stringify(JSON.parse(json), null, 2); } catch { /* store as-is */ }

  return (
    <div>
      <p className="mb-1 text-xs font-medium text-[var(--ff-text-muted)]">{label}</p>
      <pre className="max-h-64 overflow-auto rounded border border-[var(--ff-border)]
                      bg-[var(--ff-surface)] p-2 text-xs">
        {pretty}
      </pre>
    </div>
  );
}
